/**
 * Processamento de mensagens para a Central de Atendimento
 */

import { formatMessageTime } from "./formatters";
import { MEDIA_TYPES } from "./constants";

/**
 * Detecta tipo de mídia pela URL
 * @param {string} url - URL da mídia
 * @returns {string|null} Tipo de mídia
 */
export const detectMediaTypeFromUrl = (url) => {
  if (!url) return null;

  const urlLower = url.toLowerCase();

  // Imagens
  if (urlLower.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)(\?|$)/)) {
    return MEDIA_TYPES.IMAGE;
  }

  // Áudios
  if (urlLower.match(/\.(mp3|wav|ogg|oga|m4a|aac|flac)(\?|$)/)) {
    return MEDIA_TYPES.AUDIO;
  }

  // Vídeos
  if (urlLower.match(/\.(mp4|avi|mkv|mov|wmv|flv|webm|m4v)(\?|$)/)) {
    return MEDIA_TYPES.VIDEO;
  }

  // Documentos
  if (urlLower.match(/\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|rtf)(\?|$)/)) {
    return MEDIA_TYPES.DOCUMENT;
  }

  return null;
};

/**
 * Normaliza tipo de mídia da API
 * @param {string} mediaType - Tipo original da API
 * @param {string} mediaUrl - URL da mídia
 * @returns {string} Tipo normalizado
 */
export const normalizeMediaType = (mediaType, mediaUrl) => {
  if (!mediaType || mediaType === "") {
    return detectMediaTypeFromUrl(mediaUrl);
  }

  // Mapear tipos da API para tipos conhecidos
  const typeMap = {
    ptt: MEDIA_TYPES.AUDIO,
    sticker: MEDIA_TYPES.IMAGE,
    document: MEDIA_TYPES.DOCUMENT,
    video: MEDIA_TYPES.VIDEO,
    image: MEDIA_TYPES.IMAGE,
    audio: MEDIA_TYPES.AUDIO,
  };

  return typeMap[mediaType] || detectMediaTypeFromUrl(mediaUrl);
};

/**
 * Processa segmento de texto detectando imagens AWS
 * @param {string} text - Texto a processar
 * @param {object} baseMessage - Mensagem base
 * @returns {Array} Array de mensagens processadas
 */
const processSegmentForMedia = (text, baseMessage) => {
  if (!text || text.trim() === "") return [];

  const messages = [];
  let timestampIncrement = 0;

  // Regex para imagens AWS S3
  const awsImageRegex =
    /https:\/\/upsoft\.s3\.us-east-1\.amazonaws\.com\/images\/[^\s\n]+/g;
  const matches = [...text.matchAll(awsImageRegex)];

  if (matches.length === 0) {
    return [{ ...baseMessage, text }];
  }

  let lastIndex = 0;

  matches.forEach((match) => {
    // Texto antes da imagem
    if (match.index > lastIndex) {
      const textBefore = text.substring(lastIndex, match.index).trim();
      if (textBefore) {
        messages.push({
          ...baseMessage,
          text: textBefore,
          timestamp: baseMessage.timestamp + timestampIncrement,
        });
        timestampIncrement++;
      }
    }

    // Imagem como mensagem separada
    messages.push({
      ...baseMessage,
      text: `image:${match[0]}`,
      timestamp: baseMessage.timestamp + timestampIncrement,
    });
    timestampIncrement++;

    lastIndex = match.index + match[0].length;
  });

  // Texto após a última imagem
  if (lastIndex < text.length) {
    const textAfter = text.substring(lastIndex).trim();
    if (textAfter) {
      messages.push({
        ...baseMessage,
        text: textAfter,
        timestamp: baseMessage.timestamp + timestampIncrement,
      });
    }
  }

  return messages;
};

/**
 * Processa quebras de mensagem [[break]]
 * @param {string} text - Texto da mensagem
 * @param {object} baseMessage - Mensagem base
 * @returns {Array} Array de mensagens processadas
 */
export const processMessageBreaks = (text, baseMessage) => {
  if (!text || typeof text !== "string") {
    return [{ ...baseMessage, text: text || "" }];
  }

  const messages = [];
  let timestampIncrement = 0;

  // Verificar se tem [[break]]
  if (text.includes("[[break]]")) {
    const segments = text
      .split("[[break]]")
      .filter((segment) => segment.trim() !== "");

    segments.forEach((segment) => {
      const processed = processSegmentForMedia(segment.trim(), {
        ...baseMessage,
        timestamp: baseMessage.timestamp + timestampIncrement,
      });

      messages.push(...processed);
      timestampIncrement += processed.length;
    });
  } else {
    // Sem breaks, apenas processar para mídia
    const processed = processSegmentForMedia(text, baseMessage);
    messages.push(...processed);
  }

  return messages.filter((msg) => msg && msg.text && msg.text.trim() !== "");
};

/**
 * Formata mensagens da API para o formato interno
 * @param {Array} apiMessages - Mensagens da API
 * @param {string} conversationName - Nome da conversa
 * @returns {Array} Mensagens formatadas
 */
export const formatApiMessages = (apiMessages, conversationName) => {
  if (!apiMessages || !Array.isArray(apiMessages)) return [];

  const formattedMessages = [];

  apiMessages.forEach((msg) => {
    const messageContent = msg.body || msg.message || "";
    // Converter from_me para boolean de forma explícita
    // API pode retornar: true/false, 1/0, "true"/"false", etc.
    const rawFromMe = msg.from_me !== undefined ? msg.from_me : msg.is_sent;
    const isFromMe = rawFromMe === true || rawFromMe === 1 || rawFromMe === "true" || rawFromMe === "1";

    // Mapear status baseado no ack
    let messageStatus = "delivered";
    if (msg.ack !== undefined) {
      switch (msg.ack) {
        case 0:
          messageStatus = "sent";
          break;
        case 1:
          messageStatus = "delivered";
          break;
        case 2:
          messageStatus = "read";
          break;
        default:
          messageStatus = msg.status || "delivered";
      }
    } else {
      messageStatus = msg.status || "delivered";
    }

    const timestamp =
      typeof msg.timestamp === "number"
        ? msg.timestamp > 1000000000000
          ? msg.timestamp
          : msg.timestamp * 1000
        : Date.now();

    // Determinar tipo de mídia corretamente
    // A API retorna type="chat" para todas as mensagens, então só usamos para mídia real
    let mediaType = null;
    if (msg.has_media && msg.media_url) {
      // Detectar tipo pela URL ou usar um padrão
      mediaType = detectMediaTypeFromUrl(msg.media_url) || "document";
    }

    const baseMessage = {
      id: msg.id,
      sender: isFromMe ? "Me" : conversationName,
      time: formatMessageTime(timestamp),
      status: messageStatus,
      isMe: isFromMe,
      timestamp,
      has_media: msg.has_media || false,
      media_url: msg.media_url || null,
      media_type: mediaType,
    };

    // Se for mídia, não processar quebras
    if (msg.has_media) {
      formattedMessages.push({
        ...baseMessage,
        text: messageContent,
      });
    } else if (isFromMe) {
      // Mensagem enviada pelo bot/agente - processar quebras
      const processed = processMessageBreaks(messageContent, baseMessage);
      formattedMessages.push(...processed);
    } else {
      // Mensagem recebida - não processar quebras
      formattedMessages.push({
        ...baseMessage,
        text: messageContent,
      });
    }
  });

  return formattedMessages;
};

/**
 * Processa mensagem do WebSocket
 * @param {object} wsMessage - Mensagem do WebSocket
 * @returns {Array} Mensagens processadas
 */
export const processWebSocketMessage = (wsMessage) => {
  const messageText = wsMessage.message || "";
  const hasMedia = wsMessage.has_media || false;

  // Ignorar mensagens vazias sem mídia
  if (!messageText.trim() && !hasMedia) {
    return [];
  }

  const timestamp =
    typeof wsMessage.timestamp === "number"
      ? wsMessage.timestamp > 1000000000000
        ? wsMessage.timestamp
        : wsMessage.timestamp * 1000
      : Date.now();

  // Converter from_me para boolean de forma explícita
  const rawFromMe = wsMessage.from_me;
  const isFromMe = rawFromMe === true || rawFromMe === 1 || rawFromMe === "true" || rawFromMe === "1";

  // Determinar tipo de mídia corretamente (só se realmente tiver mídia)
  let mediaType = null;
  if (hasMedia && wsMessage.media_url) {
    mediaType = wsMessage.media_type || detectMediaTypeFromUrl(wsMessage.media_url) || "document";
  }

  const baseMessage = {
    id: wsMessage.id || `ws-${Date.now()}`,
    sender: isFromMe ? "Me" : wsMessage.phone,
    time: formatMessageTime(timestamp),
    status: "delivered",
    isMe: isFromMe,
    botStatus: wsMessage.bot_status || 1,
    timestamp,
    has_media: hasMedia,
    media_url: wsMessage.media_url || null,
    media_type: mediaType,
  };

  // Se for mídia, não processar quebras
  if (hasMedia) {
    return [{ ...baseMessage, text: messageText }];
  }

  // Processar quebras para mensagens enviadas
  if (isFromMe) {
    return processMessageBreaks(messageText, baseMessage);
  }

  // Mensagens recebidas - não processar quebras
  return [{ ...baseMessage, text: messageText }];
};

/**
 * Cria mensagem otimista para envio
 * @param {string} text - Texto da mensagem
 * @returns {object} Mensagem para exibição imediata
 */
export const createOptimisticMessage = (text) => {
  const timestamp = Date.now();

  return {
    id: `temp-${timestamp}`,
    text,
    sender: "Me",
    time: formatMessageTime(timestamp),
    status: "sending",
    isMe: true,
    timestamp,
    has_media: false,
    isOptimistic: true,
  };
};

/**
 * Cria mensagem de áudio otimista
 * @param {string} audioUrl - URL do áudio local
 * @param {number} duration - Duração do áudio em segundos
 * @returns {object} Mensagem de áudio
 */
export const createOptimisticAudioMessage = (audioUrl, duration = 0) => {
  const timestamp = Date.now();

  return {
    id: `temp-audio-${timestamp}`,
    text: "",
    audioUrl,
    audioDuration: duration,
    sender: "Me",
    time: formatMessageTime(timestamp),
    status: "sending",
    isMe: true,
    timestamp,
    has_media: true,
    media_type: MEDIA_TYPES.AUDIO,
    isAudio: true,
    isOptimistic: true,
  };
};

/**
 * Detecta tipo de mídia pelo arquivo
 * @param {File} file - Arquivo
 * @returns {string} Tipo de mídia
 */
export const getMediaTypeFromFile = (file) => {
  if (!file) return MEDIA_TYPES.DOCUMENT;

  const mimeType = file.type.toLowerCase();

  if (mimeType.startsWith("image/")) {
    return MEDIA_TYPES.IMAGE;
  }
  if (mimeType.startsWith("video/")) {
    return MEDIA_TYPES.VIDEO;
  }
  if (mimeType.startsWith("audio/")) {
    return MEDIA_TYPES.AUDIO;
  }

  return MEDIA_TYPES.DOCUMENT;
};

/**
 * Cria mensagem de mídia otimista (imagem, vídeo, documento)
 * @param {File} file - Arquivo a ser enviado
 * @param {string} caption - Legenda/caption opcional
 * @param {string} previewUrl - URL de preview local (para imagens/vídeos)
 * @returns {object} Mensagem de mídia
 */
export const createOptimisticMediaMessage = (file, caption = "", previewUrl = null) => {
  const timestamp = Date.now();
  const mediaType = getMediaTypeFromFile(file);

  return {
    id: `temp-media-${timestamp}`,
    text: caption,
    caption: caption,
    fileName: file.name,
    fileSize: file.size,
    sender: "Me",
    time: formatMessageTime(timestamp),
    status: "sending",
    isMe: true,
    timestamp,
    has_media: true,
    media_type: mediaType,
    media_url: previewUrl, // URL de preview local
    isOptimistic: true,
  };
};

/**
 * Verifica se texto é JSON de mídia
 * @param {string} text - Texto a verificar
 * @returns {object|null} Objeto de mídia ou null
 */
export const parseMediaJson = (text) => {
  if (!text || !text.startsWith("{")) return null;

  try {
    const parsed = JSON.parse(text);

    if (parsed.image?.url) {
      return { type: MEDIA_TYPES.IMAGE, url: parsed.image.url, caption: parsed.caption };
    }
    if (parsed.audio?.url) {
      return { type: MEDIA_TYPES.AUDIO, url: parsed.audio.url, caption: parsed.caption };
    }
    if (parsed.document?.url) {
      return {
        type: MEDIA_TYPES.DOCUMENT,
        url: parsed.document.url,
        caption: parsed.caption,
        fileName: parsed.fileName || parsed.document.fileName,
      };
    }
    if (parsed.video?.url) {
      return { type: MEDIA_TYPES.VIDEO, url: parsed.video.url, caption: parsed.caption };
    }

    return null;
  } catch {
    return null;
  }
};

/**
 * Verifica se texto tem prefixo de mídia
 * @param {string} text - Texto a verificar
 * @returns {object|null} Objeto de mídia ou null
 */
export const parseMediaPrefix = (text) => {
  if (!text) return null;

  const prefixes = ["image:", "audio:", "pdf:", "video:"];
  const prefix = prefixes.find((p) => text.startsWith(p));

  if (!prefix) return null;

  const type =
    prefix === "pdf:" ? MEDIA_TYPES.DOCUMENT : prefix.replace(":", "");
  const [, urlAndCaption] = text.split(prefix);
  const [url, ...captionParts] = urlAndCaption.trim().split(" ");
  const caption = captionParts.join(" ");

  return { type, url, caption };
};

/**
 * Agrupa mensagens por data
 * @param {Array} messages - Lista de mensagens
 * @returns {Object} Mensagens agrupadas por data
 */
export const groupMessagesByDate = (messages) => {
  if (!messages || messages.length === 0) return {};

  const groups = {};

  messages.forEach((message) => {
    const timestamp = message.timestamp || message.created_at;
    let date;

    if (typeof timestamp === "number") {
      date = new Date(timestamp > 1000000000000 ? timestamp : timestamp * 1000);
    } else {
      date = new Date(timestamp);
    }

    const dateKey = date.toDateString();

    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }

    groups[dateKey].push(message);
  });

  return groups;
};

/**
 * Verifica se deve mostrar o "rabinho" (tail) na bolha da mensagem
 * @param {Array} messages - Lista de mensagens do dia
 * @param {number} index - Índice da mensagem atual
 * @param {Function} isFromCurrentUser - Função para verificar se a mensagem é do usuário atual
 * @returns {boolean} Se deve mostrar o tail
 */
export const shouldShowTail = (messages, index, isFromCurrentUser) => {
  const currentMessage = messages[index];
  const nextMessage = messages[index + 1];

  // Mostrar tail se é a última mensagem
  if (!nextMessage) return true;

  // Mostrar tail se a próxima mensagem é de remetente diferente
  const currentIsFromMe = isFromCurrentUser(currentMessage);
  const nextIsFromMe = isFromCurrentUser(nextMessage);

  return currentIsFromMe !== nextIsFromMe;
};
